import { useEffect, useRef, useState } from 'react'
import { ChatbotService, type ConnectFlow } from '../../client'
import { apiErrorMessage } from '../../lib/api'
import { openMetaAuthPopup } from '../../lib/metaAuth'
import { useChat } from '../../providers/chat/context'
import AccountPageSelector from '../ads/AccountPageSelector'
import MetaIcon from '../ads/MetaIcon'
import Modal from '../ui/Modal'
import ChoicePrompt from './ChoicePrompt'

const CARRY_ON = 'Done, please continue.'

export default function ConnectPrompt() {
  const { brandId, activeId, connectRequest, send } = useChat()
  const [working, setWorking] = useState(false)
  const [picker, setPicker] = useState<'after_connect' | 'set_up' | null>(null)
  const [error, setError] = useState('')
  const mounted = useRef(false)
  const continued = useRef(false)

  const resolved = connectRequest?.resolved === true

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  useEffect(() => {
    if (!resolved || continued.current) return
    continued.current = true
    void send(CARRY_ON, [])
  }, [resolved, send])

  if (!connectRequest || resolved || !brandId || !activeId) return null
  const threadId = activeId

  function carryOn() {
    setPicker(null)
    void send(CARRY_ON, [])
  }

  const connectMeta = async () => {
    await openMetaAuthPopup(brandId)
    if (!mounted.current) return
    const { connect } = await ChatbotService.getThreadMessages(threadId, brandId, 1)
    if (connect?.resolved) carryOn()
    else if (connect?.flow === 'meta_ad_account') setPicker('after_connect')
    else throw new Error('Meta did not finish connecting. Please try again.')
  }

  const flows: Record<ConnectFlow, () => Promise<void>> = {
    meta_oauth: connectMeta,
    meta_ad_account: async () => setPicker('set_up'),
  }

  async function start(flow: ConnectFlow) {
    setWorking(true)
    setError('')
    try {
      await flows[flow]()
    } catch (err) {
      setError(apiErrorMessage(err))
    } finally {
      setWorking(false)
    }
  }

  const closePicker = picker === 'after_connect' ? carryOn : () => setPicker(null)

  return (
    <>
      <ChoicePrompt
        icon={<MetaIcon className="size-3.5 text-[#1877F2]" />}
        title={connectRequest.title}
        description={error ? <span className="text-red-600">{error}</span> : connectRequest.description}
        actions={[
          {
            label: connectRequest.label,
            variant: 'primary',
            disabled: working,
            onClick: () => void start(connectRequest.flow),
          },
        ]}
      />

      {picker && (
        <Modal title="Set up your ad account" onClose={closePicker}>
          <AccountPageSelector brandId={brandId} onSaved={carryOn} onBack={closePicker} />
        </Modal>
      )}
    </>
  )
}
